// ChartWithShare.tsx
import React, { useEffect, useRef } from 'react';
import CryptoJS from 'crypto-js';
import Highcharts from './highChartsSetup';
import HighchartsReact, {HighchartsReactRefObject } from 'highcharts-react-official';
import { 
  EmailIcon, 
  EmailShareButton, 
  FacebookIcon, 
  FacebookShareButton, 
  LinkedinIcon, 
  LinkedinShareButton, 
  XIcon, 
  TwitterShareButton, 
  WhatsappIcon, 
  WhatsappShareButton 
} from "react-share";
import axios from '../../app/libs/axios';


type SvgMeta = {
  title: string;
  hash: string;
  url: string;
  createdAt: string;
};

function encodeMeta(meta: SvgMeta): string {
  const json = JSON.stringify(meta);

  // Encode JSON (UTF-8) → Uint8Array
  const encoder = new TextEncoder();
  const bytes = encoder.encode(json);

  // Uint8Array → binary string
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }

  // Binary string → base64
  return btoa(binary);
}

function decodeMeta(encoded: string): SvgMeta {
  const binary = atob(encoded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  const decoder = new TextDecoder();
  const json = decoder.decode(bytes);
  return JSON.parse(json);
}

function injectMetadataIntoSvg(svg: string, meta: SvgMeta): string {
  const encoded = encodeMeta(meta);
  const metadataTag = `<metadata id="chart-meta" data-format="base64-json">${encoded}</metadata>`;
  // Insert before closing </svg>
  return svg.replace('</svg>', `${metadataTag}</svg>`);
}



interface ChartWithShareProps {
  chartOptions: any; // or `any` if you want to be loose
  title?: string;
}

const ChartWithShare: React.FC<ChartWithShareProps> = ({chartOptions, title = "Share this chart"}) => {
  const chartRef = useRef<HighchartsReactRefObject | null>(null);
  const [shareUrl, setShareUrl] = React.useState<string | null>(null);
  const [drilldownShareUrl, setDrilldownShareUrl] = React.useState<string | null>(null);
  const [isDrilldownActive, setIsDrilldownActive] = React.useState(false);

  // Helper: upload an SVG to the share API and return the share URL
  const uploadChartSvg = async (svg: string, chartTitle: string, opts: any = {}, hashSuffix = '') => {
    const hash = CryptoJS.SHA1(JSON.stringify(chartOptions) + hashSuffix).toString();
    const svgWithMeta = injectMetadataIntoSvg(svg, {
      title: chartTitle,
      hash,
      url: window.location.href,
      createdAt: new Date().toISOString(),
    });

    const response = await axios.post(
      '/upload/share-chart',
      {
        chartOptions: opts,
        svg: svgWithMeta,
        title: chartTitle,
      },
      {
        headers: { 'Content-Type': 'application/json' },
      }
    );

    return response.data?.shareUrl || null;
  };

  // Inject drilldown/drillup event handlers directly into chart options
  // This avoids timing issues with chartRef and useEffect
  const enhancedOptions = React.useMemo(() => ({
    ...chartOptions,
    chart: {
      ...chartOptions.chart,
      events: {
        ...chartOptions.chart?.events,
        drilldown: function (this: any, e: any) {
          // Call original handler if one exists
          chartOptions.chart?.events?.drilldown?.call(this, e);
          setIsDrilldownActive(true);

          // Wait for Highcharts to finish rendering the drilldown view,
          // then capture the live SVG and upload it
          setTimeout(async () => {
            try {
              const chart = chartRef.current?.chart;
              if (!chart) return;
              const ddSvg = (chart as any).getSVG();
              const baseTitle = chart.options.title?.text || title;
              const ddTitle = baseTitle + ' (drilldown)';
              const url = await uploadChartSvg(ddSvg, ddTitle, chart.options, '-dd');
              if (url) {
                setDrilldownShareUrl(url);
              }
            } catch (err) {
              console.error('Failed to upload drilldown chart:', err);
            }
          }, 500); // 500ms delay for Highcharts drilldown animation to complete
        },
        drillup: function (this: any, e: any) {
          chartOptions.chart?.events?.drillup?.call(this, e);
          setIsDrilldownActive(false);
        },
      },
    },
  }), [chartOptions]);

  // Upload the main (base) chart when chartOptions change
  // Only run when chart has actual rendered data (skip if reporter not loaded yet)
  useEffect(() => {
    const generateMainShare = async () => {
      const chart = chartRef.current?.chart;
      if (!chart) return;

      // Check the Highcharts chart instance for rendered data, not the prop
      const hasRenderedData = chart.series && chart.series.length > 0 &&
        chart.series.some(s => s.data && s.data.length > 0);
      if (!hasRenderedData) return;

      try {
        const mainSvg = (chart as any).getSVG();
        const mainTitle = chart.options.title?.text || title;
        const url = await uploadChartSvg(mainSvg, mainTitle, chart.options);
        if (url) {
          setShareUrl(url);
        }
      } catch (error) {
        console.error(error);
      }
    };

    generateMainShare();
  }, [chartOptions]);

  // Pick the correct URL based on whether user is currently in a drilldown view
  const activeShareUrl = isDrilldownActive && drilldownShareUrl ? drilldownShareUrl : shareUrl;

  return (
    <div style={{position: 'relative', width: '100%', height: '100%' }}>
      <HighchartsReact
        highcharts={Highcharts}
        options={enhancedOptions}
        ref={chartRef}
      />

      {activeShareUrl && (
        <div style={{ 
          position: 'absolute',
          bottom: 0,
          right: 8,
          alignItems: 'center', 
          display: 'flex',
          gap: '6px',
          background: 'rgba(255, 255, 255, 0.9)'
        }}>
          <span style={{ lineHeight: '16px' }}>
            Share this chart:
          </span>

          <FacebookShareButton url={activeShareUrl}>
            <FacebookIcon size={24} round />
          </FacebookShareButton>

          <TwitterShareButton url={activeShareUrl} title={title}>
            <XIcon  size={24} round />
          </TwitterShareButton>

          <LinkedinShareButton url={activeShareUrl} title={title}>
            <LinkedinIcon size={24} round />
          </LinkedinShareButton>

          <WhatsappShareButton url={activeShareUrl} title={title}>
            <WhatsappIcon size={24} round />
          </WhatsappShareButton>

          <EmailShareButton url={activeShareUrl} subject={title} body="Check out this link:">
            <EmailIcon size={24} round />
          </EmailShareButton>
        </div>
      )}
    </div>
  );
};

export default ChartWithShare
